/**
 * useModalManager - A better modal management hook that ensures clean modal states
 * 
 * Features:
 * - Only one modal open at a time (or allow specific exclusions)
 * - Auto-reset form states when modals close
 * - Proper cleanup and state isolation
 * - Simple API: open(), close(), closeAll(), getState()
 */

import { useState, useCallback } from 'react';

export const useModalManager = (initialModals = {}) => {
  // Track which modal is currently open
  const [openModal, setOpenModal] = useState(null);
  
  // Store modal-specific data/forms
  const [modalData, setModalData] = useState(initialModals);

  /**
   * Open a specific modal and close others
   */
  const openModal_fn = useCallback((modalName, data = null) => {
    // Close any currently open modal
    setOpenModal(modalName);
    
    // Set data if provided
    if (data !== null) {
      setModalData(prev => ({
        ...prev,
        [modalName]: data
      }));
    }
  }, []);

  /**
   * Close the current modal or specific modal
   */
  const closeModal = useCallback((modalName = null) => {
    if (modalName === null || modalName === openModal) {
      setOpenModal(null);
      
      // Reset the modal data
      setModalData(prev => ({
        ...prev,
        [openModal || modalName]: initialModals[openModal || modalName] || {}
      }));
    }
  }, [openModal, initialModals]);

  /**
   * Close all modals and reset data
   */
  const closeAll = useCallback(() => {
    setOpenModal(null);
    setModalData(initialModals);
  }, [initialModals]);

  /**
   * Update modal data without closing
   */
  const updateModal = useCallback((modalName, data) => {
    setModalData(prev => ({
      ...prev,
      [modalName]: { ...prev[modalName], ...data }
    }));
  }, []);

  /**
   * Check if a modal is open
   */
  const isOpen = useCallback((modalName) => {
    return openModal === modalName;
  }, [openModal]);

  /**
   * Get current state of a modal
   */
  const getModalState = useCallback((modalName) => {
    return {
      isOpen: openModal === modalName,
      data: modalData[modalName] || initialModals[modalName] || {}
    };
  }, [openModal, modalData, initialModals]);

  return {
    openModal: openModal_fn,
    closeModal,
    closeAll,
    updateModal,
    isOpen,
    getModalState,
    currentlyOpen: openModal,
    modalData
  };
};

export default useModalManager;
